<template>
  <div class="audio-recording-modal">
    <form @submit.prevent="submit">
      <div class="d-flex flex-column align-items-center justify-content-center">
        <HMSpinner class="spinner" v-if="!stream && !error" />
        <p v-if="error" class="text-danger mt-4">
          {{ error }}
        </p>
      </div>
      <AudioSetupForm
        v-if="stream || error"
        :stream="stream"
        :availableAudioInputDevices="availableAudioInputDevices"
      />
      <div class="d-flex justify-content-center align-items-center">
        <HMAudioRecorder
          v-if="stream"
          :stream="stream"
          class="d-flex justify-content-center align-items-center"
          @onRecordingAvailable="recordingComplete"
        />
      </div>
      <HMModalFooter
        class="mt-3"
        :submitButton="$t('GENERAL.BUTTON.SEND')"
        :discardButton="$t('GENERAL.BUTTON.CANCEL')"
        @close="$emit('close')"
        :disabled="!recording"
      />
    </form>
  </div>
</template>

<script>
import _ from 'lodash';
import OT from '@opentok/client';
import AudioSetupForm from './AudioSetupForm.vue';
import HMSpinner from '@/web/components/common/HMSpinner.vue';
import { mapGetters } from 'vuex';
import { HMModalFooter } from '@/web/components/common';
import HMAudioRecorder from '@/web/components/common/HMAudioRecorder';

export default {
  name: 'AudioRecordingModal',
  components: {
    AudioSetupForm,
    HMSpinner,
    HMModalFooter,
    HMAudioRecorder,
  },
  props: {
    buttonText: {
      type: String,
    },
    onRecordingAvailable: {
      type: Function,
    },
  },
  computed: {
    ...mapGetters({
      streamingProps: 'setting/streamingProps',
    }),
  },
  data() {
    return {
      stream: null,
      error: null,
      availableAudioInputDevices: [],
      recording: null,
    };
  },
  methods: {
    submit() {
      if (this.recording) {
        this.onRecordingAvailable(this.recording);
      }
      this.$emit('close');
    },
    recordingComplete(file) {
      this.recording = file;
    },
    audioInputDeviceSelected(audioInputDevice) {
      this.$store.dispatch('setting/setStreamingProps', {
        selectedAudioInputDevice: audioInputDevice,
      });
      this.updateStream();
    },
    shortenDeviceLabel(label) {
      return label.replace(/ \(\w+:\w+\) */g, '');
    },
    mapDevice(device) {
      return {
        label: this.shortenDeviceLabel(device.label),
        deviceId: device.deviceId,
      };
    },
    async getDeviceInfo() {
      await new Promise((resolve, reject) => {
        OT.getDevices((error, devices) => {
          if (error) {
            console.error('Failed to enumerate media devices: ', error);
            this.error = `${error}`;
            reject(error);
          } else {
            console.log('Got devices = ', devices);
            this.availableAudioInputDevices = devices
              .filter(device => device.kind?.toLowerCase() === 'audioinput')
              .map(this.mapDevice);
            resolve();
          }
        });
      });
    },
    async start() {
      const constraints = { audio: true };
      this.stream = await this.getUserMedia(constraints);

      await this.getDeviceInfo();
      this.updateStream();
    },
    async getUserMedia(constraints) {
      this.stream &&
        this.stream.getTracks().forEach(track => {
          track.stop();
        });
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      if (!navigator.allMediaStreams) navigator.allMediaStreams = [];
      navigator.allMediaStreams.push(stream);
      return stream;
    },
    updateStream: _.debounce(function () {
      // because setup form emits input events, this gets called too many times
      // and many streams are created, so we debounce this a bit
      this._updateStream();
    }, 700),
    async _updateStream() {
      this.error = null;
      const constraints = {
        audio: true,
      };
      const selectedAudioDevice = _.get(
        this.streamingProps.selectedAudioInputDevice,
        'deviceId',
        false
      );
      if (selectedAudioDevice) {
        constraints.audio = { deviceId: { exact: selectedAudioDevice } };
      }
      console.log('stream constraints: ', constraints);
      try {
        this.stream = await this.getUserMedia(constraints);
      } catch (error) {
        this.error = `${error}`;
        this.$notify({
          group: 'app',
          type: 'error',
          title: 'Failed to access microphone',
          text: `getUserMedia failed: ${error}`,
        });
      }
    },
  },
  async mounted() {
    await this.start();
  },
  destroyed() {
    if (this.stream) {
      this.stream.getTracks().forEach(function (track) {
        track.stop();
      });
    }
    navigator.allMediaStreams.forEach(stream => {
      stream.getTracks().forEach(function (track) {
        track.stop();
      });
    });
  },
  watch: {
    streamingProps: {
      handler(streamingProps) {
        this.updateStream();
      },
      immediate: false,
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/web/styles/components-variables.scss';

::v-deep {
  .audio-setup-form .mic-volume {
    background-color: var(--brand-color, $coral);
    background-image: var(--brand-color, $coral);
  }
}
</style>
